import { useEffect, useState } from "react";
import { MessageSquareText, Plus, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtMYT } from "@/lib/myt";
import api, { formatApiError } from "@/lib/api";
import { Stars } from "@/pages/member/MemberFeedback";

const MAX_WORDS = 50;
const countWords = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

function AddFeedbackDialog({ onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [form, setForm] = useState({ reward_name: "", category: "", rating: 0, comment: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const words = countWords(form.comment);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/admin/members?q=${encodeURIComponent(q.trim())}`).then((r) => setResults((r.data.members || r.data || []).slice(0, 8))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!client) return toast.error("Select the client");
    if (!form.reward_name.trim()) return toast.error("Reward name is required");
    if (!form.category) return toast.error("Choose a category");
    if (!form.rating) return toast.error("Select the rating");
    if (!words || words > MAX_WORDS) return toast.error(`Comment must be 1–${MAX_WORDS} words`);
    setBusy(true);
    const fd = new FormData();
    fd.append("member_id", client.id); fd.append("reward_name", form.reward_name.trim()); fd.append("category", form.category);
    fd.append("rating", String(form.rating)); fd.append("comment", form.comment.trim());
    if (file) fd.append("image", file);
    try { await api.post("/admin/feedback", fd); toast.success("Client feedback added"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="admin-feedback-dialog">
      <div className="card-lux w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-4 rounded-t-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold">Add Client Feedback</h2>
          <button onClick={onClose} aria-label="Close" data-testid="admin-feedback-close"><X className="h-5 w-5 text-[#94A3B8]" /></button>
        </div>
        <div>
          <label className="text-xs text-[#94A3B8]">Client</label>
          {client ? (
            <div className="mt-1 flex items-center justify-between rounded-lg border border-[#D4AF37]/40 px-3 py-2 text-sm text-white" data-testid="admin-feedback-client-selected">
              <span>{client.full_name} · {client.uid} · {client.email}</span>
              <button onClick={() => setClient(null)} className="text-xs text-[#94A3B8]">Change</button>
            </div>
          ) : (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, UID, username or email" data-testid="admin-feedback-client-search"
                className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-[#2a2d38] px-3 py-2 text-sm text-white" />
              {results.length > 0 && (
                <div className="mt-1 rounded-lg border border-[#2a2d38] divide-y divide-[#222530] max-h-48 overflow-y-auto">
                  {results.map((m) => (
                    <button key={m.id} onClick={() => setClient(m)} data-testid={`admin-feedback-client-${m.uid}`} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#D4AF37]/10">
                      {m.full_name} <span className="text-[#64748B] text-xs">· {m.uid} · {m.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <input value={form.reward_name} onChange={(e) => setForm({ ...form, reward_name: e.target.value })} placeholder="Reward name" data-testid="admin-feedback-reward"
          className="w-full rounded-lg bg-[#0B0C10] border border-[#2a2d38] px-3 py-2 text-sm text-white" />
        <div className="grid grid-cols-2 gap-2">
          {[["TRADE_REDEEM", "TRADE REDEEM"], ["LUCKY_DRAW", "LUCKY DRAW"]].map(([v, l]) => (
            <button key={v} onClick={() => setForm({ ...form, category: v })} aria-pressed={form.category === v} data-testid={`admin-feedback-category-${v}`}
              className={`rounded-lg border py-2 text-xs font-mono tracking-widest ${form.category === v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5D77A]" : "border-[#2a2d38] text-[#94A3B8]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`admin-feedback-star-${n}`} aria-label={`${n} star`}>
              <Star className={`h-7 w-7 ${form.rating >= n ? "text-[#F5C542] fill-[#F5C542]" : "text-[#3a3d4d]"}`} />
            </button>
          ))}
          <span className="ml-auto text-xs font-mono text-[#94A3B8]" data-testid="admin-feedback-rating-value">{form.rating} / 5</span>
        </div>
        <div>
          <textarea rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Client's comment (max 50 words)" data-testid="admin-feedback-comment"
            className="w-full rounded-lg bg-[#0B0C10] border border-[#2a2d38] px-3 py-2 text-sm text-white" />
          <p className={`text-xs text-right ${words > MAX_WORDS ? "text-red-400" : "text-[#94A3B8]"}`} data-testid="admin-feedback-word-count">{words} / {MAX_WORDS} words</p>
        </div>
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-feedback-image" className="text-xs text-[#94A3B8]" />
        <p className="text-[11px] text-[#64748B]">Recorded as admin-entered client feedback. No coin is credited for manual entries.</p>
        <button onClick={submit} disabled={busy} className="btn-gold w-full py-3 text-sm disabled:opacity-60" data-testid="admin-feedback-submit">{busy ? "Saving…" : "Save Feedback"}</button>
      </div>
    </div>
  );
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState("loading");
  const [adding, setAdding] = useState(false);

  const load = (p) => {
    setState(p === 1 ? "loading" : "more");
    api.get(`/admin/feedback?page=${p}&limit=20`)
      .then((r) => { setItems((prev) => (p === 1 ? r.data.items : [...prev, ...r.data.items])); setHasMore(r.data.has_more); setPage(p); setState("ready"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(1); }, []);

  const remove = async (f) => {
    if (!window.confirm(`Delete this feedback from ${f.member_name || "client"} for "${f.reward_name}"? It will be hidden from the member feed; coin history is kept.`)) return;
    try { await api.delete(`/admin/feedback/${f.id}`); toast.success("Feedback deleted"); setItems((prev) => prev.filter((x) => x.id !== f.id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-feedback-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h1 className="page-title">Feedback</h1><p className="page-sub mt-1">Client feedback on delivered rewards</p></div>
        <button onClick={() => setAdding(true)} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2" data-testid="admin-feedback-add"><Plus className="h-4 w-4" /> Add Client Feedback</button>
      </div>
      {state === "loading" && <p className="text-sm text-[#64748B]">Loading…</p>}
      {state === "error" && <p className="text-sm text-red-400" data-testid="admin-feedback-error">Could not load feedback.</p>}
      {state === "ready" && items.length === 0 && (
        <div className="card-lux p-10 text-center" data-testid="admin-feedback-empty"><MessageSquareText className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" /><p className="text-sm text-[#64748B]">No feedback yet.</p></div>
      )}
      <div className="card-lux divide-y divide-[#222530]" data-testid="admin-feedback-list">
        {items.map((f) => (
          <div key={f.id} className="p-4 flex gap-4" data-testid={`admin-feedback-row-${f.id}`}>
            {f.image_url && <img src={f.image_url} alt="" className="h-16 w-16 rounded-lg object-cover border border-[#222530] shrink-0" />}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-white">{f.member_name}</span>
                <span className="text-xs text-[#64748B] font-mono">{f.uid}</span>
                <span className="text-xs text-[#64748B] truncate">{f.email}</span>
                <span className={`text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border ${f.origin === "admin" ? "border-sky-400/40 text-sky-300" : "border-emerald-400/40 text-emerald-400"}`} data-testid={`admin-feedback-origin-${f.id}`}>{f.origin === "admin" ? `Admin${f.author_admin_name ? ` · ${f.author_admin_name}` : ""}` : "Member"}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Stars value={f.rating} size={12} />
                <span className="font-mono tracking-widest uppercase text-[#D4AF37]">{f.category.replace("_", " ")}</span>
                <span className="text-white">{f.reward_name}</span>
                <span className="text-[#64748B]">{fmtMYT(f.created_at)}</span>
                {f.coin_awarded && <span className="text-[#D4AF37]">+1 coin</span>}
              </div>
              <p className="text-sm text-[#94A3B8] break-words">{f.comment}</p>
            </div>
            <button onClick={() => remove(f)} className="self-start text-[#94A3B8] hover:text-red-400" aria-label="Delete feedback" data-testid={`admin-feedback-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {hasMore && <button onClick={() => load(page + 1)} disabled={state === "more"} className="btn-outline-gold w-full py-2.5 text-sm" data-testid="admin-feedback-load-more">{state === "more" ? "Loading…" : "Load more"}</button>}
      {adding && <AddFeedbackDialog onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(1); }} />}
    </div>
  );
}
